import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { 
  Bot,
  Ticket, 
  Shield, 
  TrendingUp, 
  Gift, 
  MessageSquare, 
  Users,
  Zap,
  Settings,
  Crown,
  BarChart3,
  Check,
  X,
  Loader2,
  Coins,
  ArrowLeft,
  Home,
  Search,
  AlertTriangle,
  LogOut
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useServers, useServer, usePlugins, useLeaderboard, useApiMutation, useAutomations } from "@/hooks/useApi";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { wsClient } from "@/lib/websocket";
import { CommandsContent, ReactionRolesContent, GiveawayContent } from "./DashboardComponents";
import { WelcomePlugin as WelcomeContent } from "./WelcomePlugin";
import { ModerationPlugin } from "./ModerationPlugin";
import { LevelingPlugin } from "./LevelingPlugin";
import { AutoModPlugin } from "./AutoModPlugin";
import { EconomyPlugin } from "./EconomyPlugin";
import { AIHelperPlugin } from "./AIHelperPlugin";
import { TicketsPlugin } from "./TicketsPlugin";
import { BirthdaysPlugin } from "./BirthdaysPlugin";
import { AutomationsPlugin } from "./AutomationsPlugin";
import { LoggingPlugin } from "./LoggingPlugin";
import { SuggestionsPlugin } from "./SuggestionsPlugin";
import { StarboardPlugin } from "./StarboardPlugin";
import { AntiRaidPlugin } from "./AntiRaidPlugin";
import { SocialAlertsPlugin } from "./SocialAlertsPlugin";
import { StickyRolesPlugin } from "./StickyRolesPlugin";
import { WarnsPlugin } from "./WarnsPlugin";
import { BulkRolesPlugin } from "./BulkRolesPlugin";
import { CustomCommandsPlugin } from "./CustomCommandsPlugin";
import { SelfAssignRolesPlugin } from "./SelfAssignRolesPlugin";
import { RecordingPlugin } from "./RecordingPlugin";
import { AcceptedMembersPlugin } from "./AcceptedMembersPlugin";
import { AnalyticsPlugin } from "./AnalyticsPlugin";
import { BackupsPlugin } from "./BackupsPlugin";
import { VerificationPlugin } from "./Web3GatingPlugin";
import { PollsPlugin } from "./PollsPlugin";
import { BotPersonalizerPlugin } from "./BotPersonalizerPlugin";
import { AdvertisingPlugin } from "./AdvertisingPlugin";
import { MemberExplorerPlugin } from "./MemberExplorerPlugin";
import { ServerBlacklistPlugin } from "./ServerBlacklistPlugin";
import { AIInterviewPlugin } from "./AIInterviewPlugin";
import { VoiceRecordingPlugin } from "./VoiceRecordingPlugin";
import { RulesPlugin } from "./RulesPlugin";
import { SetupAssistantPlugin } from "./SetupAssistantPlugin";
import { AIServerArchitectPlugin } from "./AIServerArchitectPlugin";
import { WelcomeDesignerPlugin } from "./WelcomeDesignerPlugin";
import { MyServers } from "./MyServers";
import { ServerManagement } from "./ServerManagement";

// Plugin Content Component - Renders the selected plugin
const PluginContent = ({
  pluginId,
  serverId,
  plugins,
  selectedServer,
  onBack,
  onHome,
}: {
  pluginId: string;
  serverId: string;
  plugins: any[];
  selectedServer: any;
  onBack: () => void;
  onHome: () => void;
}) => {
  const { t } = useI18n();
  const knownPluginIds = new Set([
    "overview",
    "moderation",
    "leveling",
    "automod",
    "welcome",
    "welcome-designer",
    "commands",
    "reaction-roles",
    "giveaway",
    "economy",
    "ai-helper",
    "tickets",
    "birthdays",
    "automations",
    "logging",
    "suggestions",
    "starboard",
    "anti-raid",
    "social-alerts",
    "sticky-roles",
    "warns",
    "bulk-roles",
    "giveaways",
    "custom-commands",
    "self-assign-roles",
    "recording",
    "accepted-members",
    "analytics",
    "backups",
    "web3-gating",
    "polls",
    "bot-personalizer",
    "advertising",
    "member-explorer",
    "server-blacklist",
    "ai-interview",
    "voice-recording",
    "rules",
    "setup-assistant",
    "ai-server-architect",
    "settings",
  ]);
  const isKnownPlugin = knownPluginIds.has(pluginId);
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* Header */}
      <header className="h-16 border-b border-border flex items-center gap-4 px-6 sticky top-0 z-10 bg-background/80 backdrop-blur-sm">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="hover:bg-muted"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          onClick={onHome}
          className="hover:bg-muted"
        >
          <Home className="w-5 h-5" />
        </Button>

        <div className="flex-1">
          <h1 className="text-xl font-bold capitalize">
            {pluginId === "web3-gating"
              ? "verification"
              : pluginId === "accepted-members"
                ? "accepted members"
                : isKnownPlugin
                  ? pluginId.replace("-", " ")
                  : "plugin"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {selectedServer?.name || t('dashboard.server')}
          </p>
        </div>

        {selectedServer?.icon && (
          <img
            src={`https://cdn.discordapp.com/icons/${selectedServer.discordId}/${selectedServer.icon}.png`}
            alt={selectedServer.name}
            className="w-10 h-10 rounded-lg"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        )}
      </header>

      {/* Main Content */}
      <main className="page-container mx-auto py-8">
        {!isKnownPlugin && (
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle>Plugin not available</CardTitle>
              <CardDescription>
                This plugin is not available in this dashboard.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={onBack} variant="outline">Go back</Button>
            </CardContent>
          </Card>
        )}

        {pluginId === "overview" && (
          <OverviewContent server={selectedServer} plugins={plugins} />
        )}
        {pluginId === "moderation" && (
          <ModerationPlugin serverId={serverId} />
        )}
        {pluginId === "leveling" && (
          <LevelingPlugin serverId={serverId} />
        )}
        {pluginId === "automod" && (
          <AutoModPlugin serverId={serverId} />
        )}
        {pluginId === "welcome" && (
          <WelcomeContent serverId={serverId} />
        )}
        {pluginId === "welcome-designer" && (
          <WelcomeDesignerPlugin serverId={serverId} />
        )}
        {pluginId === "commands" && (
          <CommandsContent serverId={serverId} />
        )}
        {pluginId === "reaction-roles" && (
          <ReactionRolesContent serverId={serverId} />
        )}
        {pluginId === "giveaway" && (
          <GiveawayContent serverId={serverId} />
        )}
        {pluginId === "economy" && (
          <EconomyPlugin serverId={serverId} />
        )}
        {pluginId === "ai-helper" && (
          <AIHelperPlugin serverId={serverId} />
        )}
        {pluginId === "tickets" && (
          <TicketsPlugin serverId={serverId} />
        )}
        {pluginId === "birthdays" && (
          <BirthdaysPlugin serverId={serverId} />
        )}
        {pluginId === "automations" && (
          <AutomationsPlugin serverId={serverId} />
        )}
        {pluginId === "logging" && (
          <LoggingPlugin serverId={serverId} />
        )}
        {pluginId === "suggestions" && (
          <SuggestionsPlugin serverId={serverId} />
        )}
        {pluginId === "starboard" && (
          <StarboardPlugin serverId={serverId} />
        )}
        {pluginId === "anti-raid" && (
          <AntiRaidPlugin serverId={serverId} />
        )}
        {pluginId === "social-alerts" && (
          <SocialAlertsPlugin serverId={serverId} />
        )}
        {pluginId === "sticky-roles" && (
          <StickyRolesPlugin serverId={serverId} />
        )}
        {pluginId === "warns" && (
          <WarnsPlugin serverId={serverId} />
        )}
        {pluginId === "bulk-roles" && (
          <BulkRolesPlugin serverId={serverId} />
        )}
        {pluginId === "giveaways" && (
          <GiveawayContent serverId={serverId} />
        )}
        {pluginId === "custom-commands" && (
          <CustomCommandsPlugin serverId={serverId} />
        )}
        {pluginId === "self-assign-roles" && (
          <SelfAssignRolesPlugin serverId={serverId} />
        )}
        {pluginId === "recording" && (
          <RecordingPlugin serverId={serverId} />
        )}
        {pluginId === "accepted-members" && (
          <AcceptedMembersPlugin serverId={serverId} />
        )}
        {pluginId === "analytics" && (
          <AnalyticsPlugin serverId={serverId} />
        )}
        {pluginId === "backups" && (
          <BackupsPlugin serverId={serverId} />
        )}
        {pluginId === "web3-gating" && (
          <VerificationPlugin serverId={serverId} />
        )}
        {pluginId === "polls" && (
          <PollsPlugin serverId={serverId} />
        )}
        {pluginId === "bot-personalizer" && (
          <BotPersonalizerPlugin serverId={serverId} />
        )}
        {pluginId === "advertising" && (
          <AdvertisingPlugin serverId={serverId} />
        )}
        {pluginId === "member-explorer" && (
          <MemberExplorerPlugin serverId={serverId} />
        )}
        {pluginId === "server-blacklist" && (
          <ServerBlacklistPlugin serverId={serverId} />
        )}
        {pluginId === "ai-interview" && (
          <AIInterviewPlugin serverId={serverId} />
        )}
        {pluginId === "voice-recording" && (
          <VoiceRecordingPlugin serverId={serverId} />
        )}
        {pluginId === "rules" && (
          <RulesPlugin serverId={serverId} />
        )}
        {pluginId === "setup-assistant" && (
          <SetupAssistantPlugin serverId={serverId} />
        )}
        {pluginId === "ai-server-architect" && (
          <AIServerArchitectPlugin serverId={serverId} />
        )}
        {pluginId === "settings" && (
          <SettingsContent />
        )}
      </main>
    </div>
  );
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  
  // Navigation states
  const [currentLevel, setCurrentLevel] = useState<'servers' | 'management' | 'plugin'>('servers');
  const [selectedServerId, setSelectedServerId] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState("overview");
  const [activeCategory, setActiveCategory] = useState("all");

  // Fetch servers
  const { data: servers = [], isLoading: serversLoading, error: serversError } = useServers();
  const { data: selectedServer, isLoading: serverLoading } = useServer(selectedServerId);
  const { data: plugins = [], isLoading: pluginsLoading } = usePlugins(selectedServerId);
  const { data: leaderboard = [], isLoading: leaderboardLoading } = useLeaderboard(selectedServerId);

  // Join WebSocket room when server is selected
  useEffect(() => {
    if (selectedServerId && wsClient.isEnabled() && wsClient.isConnected()) {
      wsClient.joinServer(selectedServerId);
      return () => {
        wsClient.leaveServer(selectedServerId);
      };
    }
  }, [selectedServerId]);

  // Listen for real-time updates
  useEffect(() => {
    if (!wsClient.isEnabled() || !wsClient.isConnected()) return;

    const handleServerUpdate = (data: any) => {
      // Invalidate queries to refetch data
      if (selectedServerId) {
        queryClient.invalidateQueries({ queryKey: ['servers', selectedServerId] });
        queryClient.invalidateQueries({ queryKey: ['servers', selectedServerId, 'plugins'] });
        queryClient.invalidateQueries({ queryKey: ['servers', selectedServerId, 'leaderboard'] });
      }
    };

    wsClient.on('server:update', handleServerUpdate);
    wsClient.on('plugin:update', handleServerUpdate);
    wsClient.on('automation:executed', handleServerUpdate);
    wsClient.on('level:up', handleServerUpdate);

    return () => {
      wsClient.off('server:update', handleServerUpdate);
      wsClient.off('plugin:update', handleServerUpdate);
      wsClient.off('automation:executed', handleServerUpdate);
      wsClient.off('level:up', handleServerUpdate);
    };
  }, [selectedServerId, queryClient]);

  const handleSelectServer = (serverId: string) => {
    setSelectedServerId(serverId);
    setCurrentLevel('management');
    setActiveSection('overview');
    setActiveCategory('all');
  };

  const handleSelectPlugin = (pluginId: string) => {
    setActiveSection(pluginId);
    setCurrentLevel('plugin');
  };

  const handleBack = () => {
    if (currentLevel === 'plugin') {
      setCurrentLevel('management');
    } else if (currentLevel === 'management') {
      setCurrentLevel('servers');
      setSelectedServerId(null);
    }
  };

  const handleGoHome = () => {
    setCurrentLevel('servers');
    setSelectedServerId(null);
    setActiveSection('overview');
    setActiveCategory('all');
  };

  const handleInviteBot = async (guildId?: string) => {
    try {
      const { serverApi } = await import('@/lib/api');
      const response = await serverApi.getBotInviteUrl();
      const inviteUrl = guildId ? `${response.url}&guild_id=${guildId}` : response.url;
      window.open(inviteUrl, '_blank');
    } catch (error) {
      console.error('Error getting invite URL:', error);
      // Fallback: construct URL manually
      const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID;
      if (clientId) {
        const inviteUrl = `https://discord.com/oauth2/authorize?client_id=${clientId}&scope=bot&permissions=8`;
        window.open(inviteUrl, '_blank');
      }
    }
  };

  return (
    <AnimatePresence mode="wait">
      {/* Blacklist Error Screen */}
      {serversError && (serversError as any)?.status === 403 && (serversError as any)?.data?.blacklistedServers && (
        <motion.div
          key="blacklist-error"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="flex items-center justify-center min-h-screen bg-gradient-to-br from-background to-background/95"
        >
          <div className="max-w-md w-full mx-4 space-y-6 text-center">
            <div className="w-24 h-24 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-12 h-12 text-red-500" />
            </div>
            <div className="space-y-3">
              <h2 className="text-3xl font-bold">{t('dashboard.accessDenied')}</h2>
              <p className="text-muted-foreground text-lg">
                {t('dashboard.blacklistReason')}
              </p>
            </div>
            
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 space-y-3 text-left">
              <p className="font-semibold text-sm text-red-400">{t('dashboard.blacklistedServers')}</p>
              {(serversError as any)?.data?.blacklistedServers?.map((server: any, idx: number) => (
                <div key={idx} className="flex items-start gap-2 text-sm">
                  <Shield className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-red-300">{server.name}</p>
                    <p className="text-red-400/80 text-xs">{t('dashboard.reason')}: {server.reason}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <p className="text-sm text-blue-300">
                {t('dashboard.blacklistRecovery')}
              </p>
            </div>

            <Button
              onClick={logout}
              variant="outline"
              className="w-full"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('nav.logout')}
            </Button>
          </div>
        </motion.div>
      )}

      {currentLevel === 'servers' && !serversError && (
        <motion.div
          key="servers"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <MyServers
            onSelectServer={handleSelectServer}
            onLogout={logout}
            user={user}
          />
        </motion.div>
      )}

      {currentLevel === 'management' && selectedServerId && (
        <motion.div
          key="management"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 20 }}
          transition={{ duration: 0.3 }}
        >
          <BotStatusGate serverId={selectedServerId} onInvite={() => handleInviteBot()}>
            <ServerManagement
              serverId={selectedServerId}
              onBack={handleBack}
              onHome={handleGoHome}
              onSelectPlugin={handleSelectPlugin}
              selectedServer={selectedServer}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
            />
          </BotStatusGate>
        </motion.div>
      )}

      {currentLevel === 'plugin' && selectedServerId && (
        <motion.div
          key="plugin"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 20 }}
          transition={{ duration: 0.3 }}
        >
          <BotStatusGate serverId={selectedServerId} onInvite={() => handleInviteBot()}>
            <PluginContent
              pluginId={activeSection}
              serverId={selectedServerId}
              plugins={Array.isArray(plugins) ? plugins : []}
              selectedServer={selectedServer}
              onBack={handleBack}
              onHome={handleGoHome}
            />
          </BotStatusGate>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Component definitions
const OverviewContent = ({ server, plugins }: { server: any; plugins: any[] }) => {
  const { t } = useI18n();
  if (!server) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const normalizedPlugins = Array.isArray(plugins) && plugins.length > 0
    ? plugins
    : Array.isArray(server?.plugins)
      ? server.plugins
      : [];
  const activePlugins = normalizedPlugins.filter((p: any) => p.enabled);

  return (
    <div className="space-y-6">
      {/* Stats Grid - Only real data */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t('dashboard.totalMembers')}
          value={server._count?.members?.toLocaleString() || "0"}
          icon={Users}
        />
        <StatCard
          title={t('dashboard.activePlugins')}
          value={activePlugins.length}
          icon={Zap}
        />
        <StatCard
          title={t('dashboard.serverName')}
          value={server.name || t('dashboard.unknown')}
          icon={Settings}
        />
        <StatCard
          title={t('dashboard.serverId')}
          value={server.discordId || "N/A"}
          icon={Bot}
        />
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Server Info */}
        <div className="glass-card p-6">
          <h3 className="font-semibold mb-4">{t('dashboard.serverInfo')}</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('dashboard.serverName')}</span>
              <span className="text-sm font-medium">{server.name || t('dashboard.unknown')}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('dashboard.members')}</span>
              <span className="text-sm font-medium">{server._count?.members?.toLocaleString() || 0}</span>
            </div>
            {server.icon && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('dashboard.icon')}</span>
                <img 
                  src={`https://cdn.discordapp.com/icons/${server.discordId}/${server.icon}.png`}
                  alt={server.name}
                  className="w-8 h-8 rounded-full"
                />
              </div>
            )}
          </div>
        </div>

        {/* Plugins Status */}
        <div className="glass-card p-6">
          <h3 className="font-semibold mb-4">{t('dashboard.pluginsStatus')}</h3>
          {normalizedPlugins.length > 0 ? (
            <div className="space-y-2">
              {activePlugins.map((plugin: any) => (
                <div key={plugin.id || plugin.pluginId} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                  <span className="text-sm font-medium">{plugin.pluginId || plugin.name}</span>
                  <Check className="w-4 h-4 text-discord-green" />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('dashboard.noActivePlugins')}</p>
          )}
        </div>
      </div>

      {/* Placeholder for future real data */}
      <div className="glass-card p-6">
        <h3 className="font-semibold mb-4">{t('dashboard.overview')}</h3>
        <div className="h-64 bg-muted/30 rounded-lg flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p className="text-sm">{t('dashboard.dataWhenActive')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ 
  title, 
  value, 
  icon: Icon 
}: { 
  title: string; 
  value: string | number; 
  icon: React.ElementType;
}) => (
  <div className="glass-card p-6">
    <div className="flex items-center justify-between mb-4">
      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary" />
      </div>
    </div>
    <p className="text-2xl font-bold">{value}</p>
    <p className="text-sm text-muted-foreground">{title}</p>
  </div>
);

// Bot Status Gate Component - Blocks dashboard access if bot not in server
const BotStatusGate = ({ 
  serverId, 
  onInvite, 
  children 
}: { 
  serverId: string; 
  onInvite: () => void;
  children: React.ReactNode;
}) => {
  const { t } = useI18n();
  const { data: botStatus, isLoading } = useQuery({
    queryKey: ['servers', serverId, 'bot-status'],
    queryFn: async () => {
      const { serverApi } = await import('@/lib/api');
      return serverApi.getBotStatus(serverId);
    },
    refetchInterval: 30000, // Check every 30 seconds
  });

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Block access if bot is not in server
  if (!botStatus?.botInServer) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[500px]">
        <div className="text-center space-y-6 max-w-md">
          <div className="w-24 h-24 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto">
            <Bot className="w-12 h-12 text-amber-500" />
          </div>
          <div>
            <h2 className="text-3xl font-bold mb-2">{t('dashboard.botMissing')}</h2>
            <p className="text-muted-foreground text-lg mb-2">
              {t('dashboard.inviteNeeded')}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('dashboard.inviteAfter')}
            </p>
          </div>
          <Button 
            onClick={onInvite} 
            size="lg"
            className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-6 text-lg"
          >
            <Bot className="w-5 h-5 mr-2" />
            {t('dashboard.inviteButton')}
          </Button>
        </div>
      </div>
    );
  }

  // Bot is in server - show dashboard content
  return <>{children}</>;
};

const PlaceholderContent = ({ section, serverId }: { section: string; serverId: string | null }) => {
  if (!serverId) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Please select a server to view {section} settings.</p>
      </div>
    );
  }
  const { data: plugins } = usePlugins(serverId);
  const { mutate: togglePlugin } = useApiMutation(
    async ({ pluginId, enabled }: { pluginId: string; enabled: boolean }) => {
      if (!serverId) {
        throw new Error('No server selected');
      }
      const { pluginApi } = await import('@/lib/api');
      if (enabled) {
        return pluginApi.enablePlugin(serverId, pluginId);
      } else {
        return pluginApi.disablePlugin(serverId, pluginId);
      }
    },
    {
      invalidateQueries: [['servers', serverId, 'plugins']],
    }
  );

  return (
    <div className="glass-card p-12 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center">
        <Settings className="w-8 h-8 text-primary" />
      </div>
      <h3 className="text-xl font-semibold mb-2 capitalize">{section.replace("-", " ")}</h3>
      <p className="text-muted-foreground max-w-md mx-auto">
        Configure your {section.replace("-", " ")} settings here. 
        This section is ready for customization.
      </p>
      {!serverId && (
        <p className="text-sm text-destructive mt-4">Please select a server first</p>
      )}
    </div>
  );
};

const SettingsContent = () => {
  const [theme, setTheme] = useState(() => localStorage.getItem('ui-theme') || 'dark');
  const [logoFrame, setLogoFrame] = useState(() => localStorage.getItem('ui-logo-frame') || 'ring');
  const [layout, setLayout] = useState(() => localStorage.getItem('ui-layout') || 'comfortable');

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    localStorage.setItem('ui-theme', theme);
  }, [theme]);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.logoFrame = logoFrame;
    localStorage.setItem('ui-logo-frame', logoFrame);
  }, [logoFrame]);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.layout = layout;
    localStorage.setItem('ui-layout', layout);
  }, [layout]);

  const themeOptions = [
    { id: 'dark', label: 'Dark' },
    { id: 'light', label: 'Light' },
    { id: 'ocean', label: 'Ocean' },
  ];

  const logoFrames = [
    { id: 'ring', label: 'Ring' },
    { id: 'glow', label: 'Glow' },
    { id: 'glass', label: 'Glass' },
    { id: 'none', label: 'None' },
  ];

  const layoutOptions = [
    { id: 'comfortable', label: 'Comfort' },
    { id: 'compact', label: 'Compact' },
    { id: 'wide', label: 'Wide' },
  ];

  return (
    <div className="space-y-6">
      <Card className="glass-card">
        <CardHeader>
          <CardTitle>Tema del sito</CardTitle>
          <CardDescription>Seleziona il tema globale della dashboard e del sito.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {themeOptions.map((option) => (
              <Button
                key={option.id}
                variant={theme === option.id ? "default" : "outline"}
                size="sm"
                onClick={() => setTheme(option.id)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="glass-card">
        <CardHeader>
          <CardTitle>Cornice logo</CardTitle>
          <CardDescription>Personalizza la cornice del logo principale.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {logoFrames.map((option) => (
              <Button
                key={option.id}
                variant={logoFrame === option.id ? "default" : "outline"}
                size="sm"
                onClick={() => setLogoFrame(option.id)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="glass-card">
        <CardHeader>
          <CardTitle>Layout</CardTitle>
          <CardDescription>Modifica densità e larghezza della UI.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {layoutOptions.map((option) => (
              <Button
                key={option.id}
                variant={layout === option.id ? "default" : "outline"}
                size="sm"
                onClick={() => setLayout(option.id)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
